import { ActionsUnion, createAction } from "swiipe.portal.shared"
import { flattenUserRelations, setUserRelationToLocalStorage } from "../../services/userRelationService"
import { IRelationSearchData } from "../../type/IRelationSearchData"
import { IRelationForOrganization } from "../../type/IRelationsForOrganization"
import { IUserRelation, IUserRelationFlattened } from "../../type/IUserRelation"
import { TUserRelation } from "../../type/TUserRelation"
import { TUserRole } from "../../type/TUserRole"
import { StoreState } from "../StoreState"

export type UserRelationState = Readonly<{
    userRelations?: IUserRelation[]
    userRelationsFlattened?: IUserRelationFlattened[]
    currentRelation?: IUserRelationFlattened
    lastUsedRelations: string[]
    relationsForOrganizations: { [orgId: string]: IRelationForOrganization[] }
    relationSearchData?: IRelationSearchData
}>

const initialState: UserRelationState = {
    relationsForOrganizations: {},
    lastUsedRelations: [],
}

export const userRelationReducerActions = {
    setUserRelations: (userRelations: IUserRelation[]) => createAction("APP/SET_USER_RELATIONS", { userRelations }),
    setCurrentRelation: (currentRelation: IUserRelationFlattened) =>
        createAction("APP/SET_CURRENT_RELATION", { currentRelation }),
    setRelationsForOrganization: (orgId: string, relations: IRelationForOrganization[]) =>
        createAction("APP/SET_RELATIONS_FOR_ORGANIZATION", { orgId, relations }),
    initLastUsedRelations: (lastUsedRelations: IUserRelationFlattened[]) =>
        createAction("APP/INIT_LASTUSED_RELATIONS", { lastUsedRelations }),
    addLastUsedRelation: (lastUsedRelationId: string) =>
        createAction("APP/ADD_NEW_ID_LASTUSED_RELATIONS", { lastUsedRelationId }),
    setRelationSearchData: (searchData: IRelationSearchData) => createAction("APP/SET_RELATION_SEARCH_DATA", { searchData }),
}

export type UserRelationActions = ActionsUnion<typeof userRelationReducerActions>

export const userRelationReducer = (state: UserRelationState = initialState, action: UserRelationActions) => {
    switch (action.type) {
        case "APP/SET_USER_RELATIONS": {
            return {
                ...state,
                userRelations: action.userRelations,
                userRelationsFlattened: flattenUserRelations(action.userRelations),
            }
        }
        case "APP/SET_CURRENT_RELATION": {
            setUserRelationToLocalStorage(action.currentRelation)
            return {
                ...state,
                currentRelation: action.currentRelation,
            }
        }
        case "APP/SET_RELATIONS_FOR_ORGANIZATION": {
            return {
                ...state,
                relationsForOrganizations: {
                    ...state.relationsForOrganizations,
                    [action.orgId]: action.relations,
                },
            }
        }
        case "APP/INIT_LASTUSED_RELATIONS": {
            return {
                ...state,
                lastUsedRelations: action.lastUsedRelations.map((relation) => relation.id).slice(0, 5),
            }
        }
        case "APP/ADD_NEW_ID_LASTUSED_RELATIONS": {
            return {
                ...state,
                lastUsedRelations: state.lastUsedRelations.includes(action.lastUsedRelationId)
                    ? [
                          state.lastUsedRelations[0],
                          ...state.lastUsedRelations.filter(
                              (id) => id === action.lastUsedRelationId && id !== state.lastUsedRelations[0]
                          ),
                          ...state.lastUsedRelations.filter(
                              (id) => id !== action.lastUsedRelationId && id !== state.lastUsedRelations[0]
                          ),
                      ]
                    : [state.lastUsedRelations[0], action.lastUsedRelationId, ...state.lastUsedRelations.slice(1, 4)],
            }
        }
        case "APP/SET_RELATION_SEARCH_DATA": {
            return {
                ...state,
                relationSearchData: action.searchData,
            }
        }
    }

    return state
}

export const userRelationSelectors = {
    userRelationsFlattened: (state: StoreState) => state.userRelation.userRelationsFlattened,
    userRelations: (state: StoreState) => state.userRelation.userRelations,
    currentUserRelation: (state: StoreState) => state.userRelation.currentRelation,
    currentUserRelationType: (state: StoreState): TUserRelation =>
        state.userRelation.currentRelation ? state.userRelation.currentRelation.relationType : "User",
    currentUserRelationRole: (state: StoreState): TUserRole =>
        state.userRelation.currentRelation ? state.userRelation.currentRelation.userRole : TUserRole.None,
    relationsForOrganization: (state: StoreState, orgId: string): IRelationForOrganization[] =>
        state.userRelation.relationsForOrganizations[orgId],
    lastUsedRelations: (state: StoreState) => state.userRelation.lastUsedRelations,
    getRelationSearchData: (state: StoreState) => state.userRelation.relationSearchData,
}
